/**
 * EditTenantDialog Component
 *
 * Modal form for editing an existing tenant's information — org name,
 * subscription status, entitlements, and the optional numeric limits — via
 * `PATCH /api/tenant-management/orgs/{orgId}`.
 *
 * The Customer ID is shown read-only: it is the immutable billing anchor set at
 * creation and the backend does not accept changes to it. The primary domain is
 * likewise read-only (ZITADEL models domains as separate org resources).
 *
 * Gated by the caller's `canManageTenants` authority at the call site (the Edit
 * button only renders for capable staff); the backend re-enforces it.
 *
 * @module components/EditTenantDialog
 */

import { useCallback, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Alert,
  Box,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import type { TenantDetails, UpdateTenantRequest } from '../types';
import { SUBSCRIPTION_STATUSES } from '../types';
import { useTenantsStore } from '../stores/useTenantsStore';
import { useOrgsStore } from '../stores/useOrgsStore';
import { formatErrorMessage } from '../utils';
import { isHttpError } from '../api/http';

// =============================================================================
// Types
// =============================================================================

export interface EditTenantDialogProps {
  /**
   * The tenant to edit. `null` keeps the dialog closed; a non-null value opens
   * it prefilled. Passing the tenant (rather than a separate `open` flag) keeps
   * the form seed and the open state in lockstep.
   */
  tenant: TenantDetails | null;
  /** Called to close (cancel or after a successful save). */
  onClose: () => void;
  /** Called after a successful save, with a confirmation message. */
  onSuccess?: (message: string) => void;
}

// =============================================================================
// Component
// =============================================================================

/**
 * Numeric field parsing: an empty string or non-numeric input means "no limit"
 * → 0 (the backend's "not set" sentinel), matching CreateTenantDialog.
 */
function toCount(raw: string): number {
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** Seed value for a numeric-limit input: 0 ("no limit") renders as blank. */
function fromCount(n: number): string {
  return n > 0 ? String(n) : '';
}

export function EditTenantDialog({
  tenant,
  onClose,
  onSuccess,
}: EditTenantDialogProps) {
  const [orgName, setOrgName] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState('');

  // Entitlements (chips) + the pending entry being typed
  const [entitlements, setEntitlements] = useState<string[]>([]);
  const [entitlementDraft, setEntitlementDraft] = useState('');

  // Optional numeric limits (kept as strings for controlled inputs)
  const [seatLimit, setSeatLimit] = useState('');
  const [projectLimit, setProjectLimit] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateTenant = useTenantsStore((state) => state.updateTenant);
  const loadOrgs = useOrgsStore((state) => state.loadOrgs);

  const open = tenant !== null;

  // Seed the form from the tenant each time the dialog transitions to open
  // (React-recommended previous-prop pattern, matching CreateTenantDialog).
  // prevOpen starts false — not `open` — so a dialog MOUNTED already-open (its
  // state seeds from blank defaults, unlike the create dialog's) still seeds.
  const [prevOpen, setPrevOpen] = useState(false);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open && tenant) {
      setOrgName(tenant.name);
      // The backend guarantees a valid status on details, but an org created
      // before the metadata existed can have an empty one — fall back to the
      // creation default so the select always holds a valid option.
      setSubscriptionStatus(tenant.subscriptionStatus || 'trialing');
      setEntitlements(tenant.entitlements ?? []);
      setEntitlementDraft('');
      setSeatLimit(fromCount(tenant.seatLimit));
      setProjectLimit(fromCount(tenant.projectLimit));
      setError(null);
      setIsSubmitting(false);
    }
  }

  const requiredFilled = orgName.trim() !== '';
  const canSubmit = requiredFilled && !isSubmitting;

  const addEntitlement = useCallback(() => {
    const value = entitlementDraft.trim();
    if (value === '') return;
    setEntitlements((prev) => (prev.includes(value) ? prev : [...prev, value]));
    setEntitlementDraft('');
  }, [entitlementDraft]);

  const removeEntitlement = useCallback((value: string) => {
    setEntitlements((prev) => prev.filter((e) => e !== value));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!tenant || !canSubmit) return;
    setIsSubmitting(true);
    setError(null);

    // Send every editable field; the endpoint is a partial PATCH, but the form
    // holds full state, so the request simply reflects the form. customerId is
    // never part of the payload — the backend doesn't accept it.
    const req: UpdateTenantRequest = {
      orgName: orgName.trim(),
      subscriptionStatus,
      entitlements,
      seatLimit: toCount(seatLimit),
      projectLimit: toCount(projectLimit),
    };

    try {
      await updateTenant(tenant.id, req);
      // A rename changes the picker label — refresh the list so it agrees.
      await loadOrgs();
      onClose();
      onSuccess?.(`Tenant "${req.orgName}" updated.`);
    } catch (err) {
      // 409 = rename collision (or a protected org — defensive; the Edit button
      // isn't shown for those); everything else is a generic failure.
      if (isHttpError(err) && err.status === 409) {
        setError('A tenant with this name already exists.');
      } else {
        setError(formatErrorMessage(err));
      }
      setIsSubmitting(false);
    }
  }, [
    tenant,
    canSubmit,
    orgName,
    subscriptionStatus,
    entitlements,
    seatLimit,
    projectLimit,
    updateTenant,
    loadOrgs,
    onClose,
    onSuccess,
  ]);

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      data-testid="edit-tenant-dialog"
    >
      <DialogTitle data-testid="edit-tenant-dialog-title">
        Edit tenant
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
          {error && (
            <Alert severity="error" data-testid="edit-tenant-error">
              {error}
            </Alert>
          )}

          <Typography
            variant="overline"
            sx={{
              color: 'text.secondary',
            }}
          >
            Organization
          </Typography>
          <TextField
            label="Organization name"
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            disabled={isSubmitting}
            required
            fullWidth
            slotProps={{
              htmlInput: { 'data-testid': 'edit-tenant-org-name' },
            }}
          />
          <TextField
            label="Customer ID"
            value={tenant?.customerId ?? ''}
            disabled
            fullWidth
            helperText="The customer ID cannot be changed"
            slotProps={{
              htmlInput: { 'data-testid': 'edit-tenant-customer-id' },
            }}
          />
          <TextField
            select
            label="Subscription status"
            value={subscriptionStatus}
            onChange={(e) => setSubscriptionStatus(e.target.value)}
            disabled={isSubmitting}
            fullWidth
            slotProps={{
              htmlInput: { 'data-testid': 'edit-tenant-subscription' },
            }}
          >
            {SUBSCRIPTION_STATUSES.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>

          {/* Entitlements — add-on-enter chips */}
          <Box>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'flex-start',
              }}
            >
              <TextField
                label="Entitlement"
                placeholder="llm.platform_keys"
                value={entitlementDraft}
                onChange={(e) => setEntitlementDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addEntitlement();
                  }
                }}
                disabled={isSubmitting}
                fullWidth
                size="small"
                slotProps={{
                  htmlInput: { 'data-testid': 'edit-tenant-entitlement-input' },
                }}
              />
              <Button
                onClick={addEntitlement}
                disabled={isSubmitting || entitlementDraft.trim() === ''}
                data-testid="edit-tenant-entitlement-add"
                sx={{ mt: 0.5 }}
              >
                Add
              </Button>
            </Stack>
            {entitlements.length > 0 && (
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                sx={{
                  flexWrap: 'wrap',
                  mt: 1,
                }}
              >
                {entitlements.map((e) => (
                  <Chip
                    key={e}
                    label={e}
                    onDelete={
                      isSubmitting ? undefined : () => removeEntitlement(e)
                    }
                    data-testid={`edit-tenant-entitlement-chip-${e}`}
                  />
                ))}
              </Stack>
            )}
          </Box>

          {/* Optional numeric limits (0 / blank = no limit) */}
          <Stack direction="row" spacing={2}>
            <TextField
              label="Seat limit"
              type="number"
              value={seatLimit}
              onChange={(e) => setSeatLimit(e.target.value)}
              disabled={isSubmitting}
              fullWidth
              slotProps={{
                htmlInput: { min: 0, 'data-testid': 'edit-tenant-seat-limit' },
              }}
            />
            <TextField
              label="Project limit"
              type="number"
              value={projectLimit}
              onChange={(e) => setProjectLimit(e.target.value)}
              disabled={isSubmitting}
              fullWidth
              slotProps={{
                htmlInput: { min: 0, 'data-testid': 'edit-tenant-project-limit' },
              }}
            />
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          disabled={isSubmitting}
          data-testid="edit-tenant-cancel"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSubmit}
          data-testid="edit-tenant-submit"
        >
          {isSubmitting ? 'Saving…' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
